import type { NoticeOpportunityActivity } from "@/lib/api/mentorships";

export type NoticeBadgeKind =
  | "needsResponse"
  | "dayOneTwo"
  | "birthday"
  | "checkIn"
  | "riskOfPause"
  | "momentHub"
  | "momentum";

export interface NoticeBadge {
  kind: NoticeBadgeKind;
  label: string;
}

export interface NoticeMentorshipSignals {
  birthday: boolean;
  momentHubKind: "activity" | "content" | "moment" | null;
  dayState: "day1" | "day2" | null;
  momentum: boolean;
  opportunityActivityKind: NoticeOpportunityActivity | null;
  checkIn: boolean;
  opportunityHub: boolean;
  riskOfPause: boolean;
}

export interface NoticeBadgeSignals extends NoticeMentorshipSignals {
  /** Last inbound is unanswered, or a pending activity/Hub post needs a reply. */
  needsResponse?: boolean;
}

export function isPendingNeedsResponseKind(
  kind: NoticeMentorshipSignals["momentHubKind"],
): boolean {
  return kind === "activity" || kind === "content";
}

export function conversationNeedsResponse(
  lastMessageFromPeer: boolean,
  mentorship?: NoticeMentorshipSignals,
  mentorResponded = false,
): boolean {
  if (lastMessageFromPeer) {
    return true;
  }
  if (mentorResponded) {
    return false;
  }
  return isPendingNeedsResponseKind(mentorship?.momentHubKind ?? null);
}

interface NoticeBadgeDefinition {
  kind: NoticeBadgeKind;
  matches: (signals: NoticeBadgeSignals) => boolean;
  label: (signals: NoticeBadgeSignals) => string;
}

const NOTICE_BADGES: readonly NoticeBadgeDefinition[] = [
  {
    kind: "needsResponse",
    matches: (s) => Boolean(s.needsResponse),
    label: () => "Needs response",
  },
  {
    kind: "dayOneTwo",
    matches: (s) => s.dayState !== null,
    label: (s) => (s.dayState === "day1" ? "Day 1" : "Day 2"),
  },
  { kind: "birthday", matches: (s) => s.birthday, label: () => "Birthday" },
  { kind: "checkIn", matches: (s) => s.checkIn, label: () => "Check-in" },
  {
    kind: "riskOfPause",
    matches: (s) => s.riskOfPause,
    label: () => "Risk of pause",
  },
  {
    kind: "momentHub",
    matches: (s) => s.momentHubKind === "moment",
    label: () => "Moment",
  },
  { kind: "momentum", matches: (s) => s.momentum, label: () => "Momentum" },
];

export function resolveNoticeBadge(
  signals: NoticeBadgeSignals,
): NoticeBadge | null {
  for (const definition of NOTICE_BADGES) {
    if (definition.matches(signals)) {
      return { kind: definition.kind, label: definition.label(signals) };
    }
  }
  return null;
}

export function noticeBadgeRank(badge: NoticeBadge | null): number {
  if (badge === null) {
    return NOTICE_BADGES.length;
  }
  return NOTICE_BADGES.findIndex((definition) => definition.kind === badge.kind);
}
